import { useEffect, useRef, type ReactNode, type ElementType } from "react";

type Effect = "up" | "fade" | "scale" | "left" | "right" | "clip" | "rotate";

interface RevealProps {
  children: ReactNode;
  effect?: Effect;
  stagger?: boolean;
  as?: ElementType;
  className?: string;
  delay?: number;
  threshold?: number;
}

export function Reveal({
  children,
  effect = "up",
  stagger = false,
  as,
  className = "",
  delay = 0,
  threshold = 0.1,
}: RevealProps) {
  const Tag = (as ?? "div") as ElementType;
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (delay) {
              window.setTimeout(() => el.classList.add("in-view"), delay);
            } else {
              el.classList.add("in-view");
            }
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px 100px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay, threshold]);

  const props: Record<string, unknown> = {
    ref,
    className,
  };
  if (stagger) props["data-stagger"] = "";
  else props["data-reveal"] = effect;

  return <Tag {...props}>{children}</Tag>;
}
